/**
 * Attendance Create Page — hardcoded form for marking attendance.
 */

import * as React from 'react';
import { useNavigate } from 'react-router';
import { useCreate } from '@refinedev/core';
import {
  Button, Card, CardContent, Input, Label, Textarea,
} from '@yesbos/ui';
import { ArrowLeft } from 'lucide-react';

const STATUS_OPTIONS = ['present', 'absent', 'half_day', 'late', 'on_leave', 'holiday', 'work_from_home'];

export function AttendanceCreatePage() {
  const navigate = useNavigate();
  const { mutate: create, isLoading } = useCreate();
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = React.useState({
    employee_id: '', date: today, check_in_time: '', check_out_time: '',
    status: 'present', marked_by: 'admin', remarks: '',
  });
  const set = (f: string, v: unknown) => setForm(p => ({ ...p, [f]: v }));

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload = {
      ...form,
      check_in_time: form.check_in_time ? `${form.date}T${form.check_in_time}:00` : null,
      check_out_time: form.check_out_time ? `${form.date}T${form.check_out_time}:00` : null,
    };
    create(
      { resource: 'attendance_logs', values: payload },
      { onSuccess: () => navigate('/attendance') },
    );
  }

  return (
    <div className="p-6 max-w-xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/attendance')}><ArrowLeft className="h-4 w-4" /></Button>
        <h1 className="text-2xl font-semibold">Mark Attendance</h1>
      </div>
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="employee_id">Employee ID *</Label>
              <Input id="employee_id" value={form.employee_id} onChange={e => set('employee_id', e.target.value)} required placeholder="Employee UUID" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="date">Date *</Label>
                <Input id="date" type="date" value={form.date} onChange={e => set('date', e.target.value)} required />
              </div>
              <div className="space-y-1">
                <Label htmlFor="status">Status</Label>
                <select
                  id="status"
                  className="w-full border border-input bg-background rounded-md px-3 py-2 text-sm"
                  value={form.status}
                  onChange={e => set('status', e.target.value)}
                >
                  {STATUS_OPTIONS.map(s => (
                    <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="check_in_time">Check-In Time</Label>
                <Input id="check_in_time" type="time" value={form.check_in_time} onChange={e => set('check_in_time', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="check_out_time">Check-Out Time</Label>
                <Input id="check_out_time" type="time" value={form.check_out_time} onChange={e => set('check_out_time', e.target.value)} />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="remarks">Remarks</Label>
              <Textarea id="remarks" value={form.remarks} onChange={e => set('remarks', e.target.value)} rows={2} />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => navigate('/attendance')}>Cancel</Button>
              <Button type="submit" disabled={isLoading}>{isLoading ? 'Saving...' : 'Mark Attendance'}</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default AttendanceCreatePage;
